import Vector from "victor";
import WhiteboardControllerInterface from "./types/WhiteboardControllerInterface";

export interface SimplePointerEventHandlerDelegate {
  onPointerDown(point: Vector): void;
  onPointerMove(point: Vector): void;
  onPointerUp(): void;
  abort(): void;
  setIsShiftDown?(isShiftDown: boolean): void;
}

function shouldIgnore(e: MouseEvent | TouchEvent): boolean {
  const target = e.target as HTMLElement;
  if (!target) return false;
  if (target.nodeName.toLowerCase() !== "p") {
    return false;
  }
  // Ignore nodes with text selection enabled
  let cursor: HTMLElement | null = target;
  while (cursor) {
    if (cursor.className.indexOf("NoPointerEventsPlease") !== -1) {
      return true;
    }
    cursor = cursor.parentElement;
  }
  return false;
}

/**
 * Sets up event handlers for tools that just want you to do a simple
 * click-and-drag operation
 */
export class SimplePointerEventHandler {
  // Populated by bind()
  wb?: WhiteboardControllerInterface;

  delegate?: SimplePointerEventHandlerDelegate;

  isEnabled = true;
  didCatchMouseDown = true;

  clientPoint: Vector = new Vector(0, 0);

  onMouseDown: (e: MouseEvent) => void;
  onMouseMove: (e: MouseEvent) => void;
  onMouseUp: (e: MouseEvent) => void;
  onTouchStart: (e: TouchEvent) => void;
  onTouchMove: (e: TouchEvent) => void;
  onTouchEnd: (e: TouchEvent) => void;
  onTouchCancel: (e: TouchEvent) => void;

  constructor(private triggerThumbnails: boolean = true) {
    this.onMouseDown = (e: MouseEvent) => {
      if (!this.isEnabled) return;
      this.updateIsShiftDown(e.shiftKey);
      this.didCatchMouseDown = false;
      // Discard events starting on p elements so as not to mess with the text editor
      if (shouldIgnore(e)) return;
      this.didCatchMouseDown = true;
      if (this.triggerThumbnails) this.wb?.cancelThumbnailUpload();
      this.delegate?.onPointerDown(this.point(e));
    };

    this.onMouseMove = (e: MouseEvent) => {
      if (!this.isEnabled || !this.didCatchMouseDown) return;
      this.updateIsShiftDown(e.shiftKey);
      this.delegate?.onPointerMove(this.point(e));
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    this.onMouseUp = (e: MouseEvent) => {
      if (!this.isEnabled || !this.didCatchMouseDown) return;
      if (this.triggerThumbnails) this.wb?.scheduleThumbnailUpload();
      this.updateIsShiftDown(e.shiftKey);
      this.delegate?.onPointerUp();
    };

    this.onTouchStart = (e: TouchEvent) => {
      if (!this.isEnabled) return;
      this.updateIsShiftDown(e.shiftKey);
      if (e.touches.length > 1) {
        this.delegate?.abort();
      }
      this.didCatchMouseDown = false;
      // Discard events starting on p elements so as not to mess with the text editor
      if (shouldIgnore(e)) return;
      this.didCatchMouseDown = true;
      if (this.triggerThumbnails) this.wb?.cancelThumbnailUpload();
      this.delegate?.onPointerDown(this.mpoint(e));
    };

    this.onTouchMove = (e: TouchEvent) => {
      if (!this.isEnabled || !this.didCatchMouseDown) return;
      this.updateIsShiftDown(e.shiftKey);
      this.delegate?.onPointerMove(this.mpoint(e));
    };

    // eslint-disable-next-line
    this.onTouchEnd = (e: TouchEvent) => {
      if (!this.isEnabled || !this.didCatchMouseDown) return;
      if (this.triggerThumbnails) this.wb?.scheduleThumbnailUpload();
      this.updateIsShiftDown(e.shiftKey);
      this.delegate?.onPointerUp();
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    this.onTouchCancel = (e: TouchEvent) => {
      if (!this.isEnabled || !this.didCatchMouseDown) return;
      if (this.triggerThumbnails) this.wb?.scheduleThumbnailUpload();
      this.updateIsShiftDown(e.shiftKey);
      this.delegate?.abort();
    };
  }

  private updateIsShiftDown(isShiftDown: boolean) {
    if (this.delegate?.setIsShiftDown)
      this.delegate.setIsShiftDown(isShiftDown);
  }

  bind(
    wb: WhiteboardControllerInterface,
    delegate: SimplePointerEventHandlerDelegate
  ) {
    this.wb = wb;
    this.delegate = delegate;
    wb.el.addEventListener("mousedown", this.onMouseDown);
    wb.el.addEventListener("mousemove", this.onMouseMove);
    wb.el.addEventListener("mouseup", this.onMouseUp);
    wb.el.addEventListener("mouseleave", this.onMouseUp);
    wb.el.addEventListener("touchstart", this.onTouchStart);
    wb.el.addEventListener("touchmove", this.onTouchMove);
    wb.el.addEventListener("touchend", this.onTouchEnd);
    wb.el.addEventListener("touchcancel", this.onTouchCancel);
  }

  unbind() {
    this.delegate = undefined;
    if (!this.wb) return;
    this.wb.el.removeEventListener("mousedown", this.onMouseDown);
    this.wb.el.removeEventListener("mousemove", this.onMouseMove);
    this.wb.el.removeEventListener("mouseup", this.onMouseUp);
    this.wb.el.removeEventListener("mouseleave", this.onMouseUp);
    this.wb.el.removeEventListener("touchstart", this.onTouchStart);
    this.wb.el.removeEventListener("touchmove", this.onTouchMove);
    this.wb.el.removeEventListener("touchend", this.onTouchEnd);
    this.wb.el.removeEventListener("touchcancel", this.onTouchCancel);
    this.wb = undefined;
  }

  /**
   * Convert a mouse event to a screen space vector
   */
  point(e: MouseEvent) {
    this.clientPoint = new Vector(e.clientX, e.clientY);
    if (!this.wb) return new Vector(e.clientX, e.clientY);
    return new Vector(
      e.clientX - this.wb.el.offsetLeft,
      e.clientY - this.wb.el.offsetTop
    );
  }

  mpoint(e: TouchEvent) {
    if (!this.wb) return new Vector(e.touches[0].clientX, e.touches[0].clientY);

    this.clientPoint = new Vector(e.touches[0].clientX, e.touches[0].clientY);
    return new Vector(
      e.touches[0].clientX - this.wb.el.offsetLeft,
      e.touches[0].clientY - this.wb.el.offsetTop
    );
  }
}
